// src/pages/SpaceSearchResultPage.tsx

import React, { useEffect, useState, useRef } from 'react'; 
import {
  Box,
  Grid,
  Typography,
  Card,
  Chip,
  IconButton,
  Stack,
  CircularProgress,
  Dialog,
  DialogContent,
  Button,
  GlobalStyles,
  Popover,
  Divider,
  Rating,
  Tooltip,
} from '@mui/material';
import { useParams, useNavigate, useLocation, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { toUrlFriendly, urlFriendlyEquals } from '../../utils/urlUtils';
import { resolveMarketingCanonicalForAppPath } from '../../utils/marketingCanonical';
import { resolvePublicSpaceBySlug, schoolTutorPagePath } from '../../utils/publicSpaceResolution';
import { isSpace24hOpen } from '../../utils/spaceUtils';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
// no arrow icon; use Chip like on room page
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import InstagramIcon from '@mui/icons-material/Instagram';
import FacebookIcon from '@mui/icons-material/Facebook';
import SendIcon from '@mui/icons-material/Send';
import LinkIcon from '@mui/icons-material/Link';
import LanguageIcon from '@mui/icons-material/Language';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import ShareIcon from '@mui/icons-material/Share';
import { useTranslation } from 'react-i18next';
import { estimateHourlyBookingValue, trackStartBooking, trackViewProvider } from '../../utils/analytics';

import AddIcon from '@mui/icons-material/Add';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import { GoogleMap, Marker } from '@react-google-maps/api';
import { useGoogleMaps } from '../../contexts/GoogleMapsContext';

import { apiClient } from '../../publicApiClient';
import { tutorApiClient } from '../../tutorApiClient';
import { SpaceSummary } from '../../models/Space';
import { ContactType } from '../../models/Contact';
import { getAmenityLabel, amenityIcons } from '../../amenities';
import { getGearLabel, gearIcons } from '../../gears';
import { getCategoryLabel } from '../../categories';
import SEO from '../../components/SEO';
import { seoService, SEOData } from '../../services/seoService';
import { Review } from '../../models/CreateReviewRequest';
import MediaDisplay from '../../components/MediaDisplay';
import RoomBookingDialog from '../../components/RoomBookingDialog';
import { RoomSummary } from '../../models/Room';
import { isRoomBookingAllowed, roomBookingBlockedHint } from '../../utils/roomBookingAccess';
import VideoPlayer from '../../components/VideoPlayer';
import { isVideoUrl } from '../../utils/mediaUtils';
import { NotificationSnackbar } from '../../components/common/NotificationSnackbar';
import { useExperimentalFeatures } from '../../contexts/ExperimentalFeaturesContext';
import PassOfferList from '../../components/passes/PassOfferList';
import PassPurchaseConfirmDialog from '../../components/passes/PassPurchaseConfirmDialog';
import PassCheckoutResumeErrorAlert from '../../components/passes/PassCheckoutResumeErrorAlert';
import { PassOfferSummary } from '../../models/Pass';
import {
  isPassPurchaseAvailableForRooms,
  resolvePassCheckoutPaymentMethodsForRooms,
} from '../../utils/passPurchaseAvailability';
import { resolvePassOfferTargetNames } from '../../utils/passFormatting';
import {
  readRoomBookingResumeFromSearchParams,
  stripRoomBookingResumeSearchParams,
} from '../../utils/bookingLoginResume';

export default function SpaceSearchResultPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { city, spaceName, roomName } = useParams<{ city: string; spaceName: string; roomName?: string }>();
  const { t } = useTranslation();
  const { enabled: experimentalFeaturesEnabled } = useExperimentalFeatures();
  

  const [space, setSpace] = useState<SpaceSummary | null>(null);
  const [seoData, setSeoData] = useState<SEOData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [roomSteps, setRoomSteps] = useState<Record<string, number>>({});
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const [roomGallery, setRoomGallery] = useState<{ room: RoomSummary; index: number } | null>(null);
  const [reviews, setReviews] = useState<Review[]>([]);
  const [reviewStats, setReviewStats] = useState<{ averageRating: number; reviewCount: number } | null>(null);
  const { isLoaded, loadError } = useGoogleMaps();
  const [bookingOpen, setBookingOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState<RoomSummary | null>(null);
  const [initialDateTime, setInitialDateTime] = useState<Date | undefined>(undefined);
  const [initialDuration, setInitialDuration] = useState<number | undefined>(undefined);
  const [initialStepKey, setInitialStepKey] = useState<'whoIsComing' | null>(null);
  const [showAllReviews, setShowAllReviews] = useState(false);
  const howToFindRef = useRef<HTMLDivElement>(null);
  const didAutoOpenBookingRef = useRef(false);
  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverContent, setPopoverContent] = useState<{
    icon: JSX.Element;
    label: string;
    details?: string;
    price?: number;
    currency?: string;
  } | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [cities, setCities] = useState<string[]>([]);
  const [hasTutors, setHasTutors] = useState(false);
  const [passOffers, setPassOffers] = useState<PassOfferSummary[]>([]);
  const [passOffersLoading, setPassOffersLoading] = useState(false);
  const [selectedPassOffer, setSelectedPassOffer] = useState<PassOfferSummary | null>(null);
  const [passDialogOpen, setPassDialogOpen] = useState(false);

  const mapContainerStyle = {
    width: '100%',
    height: '100%',
    minHeight: '200px',
    position: 'relative' as const
  };



  const [citiesLoaded, setCitiesLoaded] = useState(false);

  // Fetch cities on component mount
  useEffect(() => {
    const fetchCities = async () => {
      try {
        const citiesList = await apiClient.getCities();
        setCities(citiesList);
        setCitiesLoaded(true);
      } catch (error) {
        console.error('Failed to fetch cities:', error);
        setCities(['Warszawa']); // Fallback
        setCitiesLoaded(true); // Still mark as loaded even if failed
      }
    };
    fetchCities();
  }, []);

  useEffect(() => {
    console.log("useEffect hook triggered with city:", city, "spaceName:", spaceName);

    if (!city || !spaceName) {
      console.error("No city or spaceName found in useEffect, aborting API call.");
      setLoading(false);
      setError(true);
      return;
    }

    // Wait for cities to load before fetching page data to ensure correct city name conversion
    if (!citiesLoaded) {
      return;
    }

    const fetchPageData = async () => {
      setLoading(true);
      setError(false);
      try {
        const resolvedSpace = await resolvePublicSpaceBySlug(city, spaceName, cities);

        if (!resolvedSpace) {
          console.error("No matching space found for city:", city, "spaceName:", spaceName);
          setError(true);
          setLoading(false);
          return;
        }

        let spaceData = resolvedSpace.summary;

        if (localStorage.getItem('access_token') && spaceData.rooms?.length) {
          try {
            const summaries = await Promise.all(spaceData.rooms.map((r) => apiClient.getRoomSummary(r.id)));
            spaceData = {
              ...spaceData,
              rooms: spaceData.rooms.map((r, i) => ({
                ...r,
                bookingAccess: summaries[i]?.bookingAccess ?? (r as RoomSummary).bookingAccess,
              })),
            };
          } catch (e) {
            console.warn('Could not load per-room booking access', e);
          }
        }

        setSpace(spaceData);
        const hourlyRates = spaceData.rooms
          .map((room) => Number(room.pricePerH))
          .filter((price) => Number.isFinite(price) && price > 0);
        trackViewProvider({
          provider_type: 'space',
          provider_id: spaceData.id,
          city: spaceData.city,
          provider_name: spaceData.name,
          currency: String(spaceData.currencyCode ?? 'PLN'),
          ...(hourlyRates.length > 0 ? { value: Math.min(...hourlyRates) } : {}),
        });

        // initialize carousel steps
        const initial: Record<string, number> = {};
        spaceData.rooms.forEach((r) => {
          initial[r.id] = 0;
        });
        setRoomSteps(initial);

        // Fetch reviews for the space
        try {
          const spaceReviews = await apiClient.getReviews(resolvedSpace.searchResult.id);
          setReviews(spaceReviews);
          
          // Calculate review statistics
          if (spaceReviews.length > 0) {
            const totalRating = spaceReviews.reduce((sum, review) => sum + review.rating, 0);
            const averageRating = totalRating / spaceReviews.length;
            setReviewStats({
              averageRating,
              reviewCount: spaceReviews.length
            });
          }
        } catch (error) {
          console.log('No reviews found or error fetching reviews:', error);
        }

      } catch (err) {
        console.error("Failed to fetch critical space data:", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    
    const fetchSeo = async () => {
        if (!city || !spaceName) return;
        try {
            // For SEO, we'll use the city/spaceName combination as the identifier
            const spaceIdentifier = `${city}/${spaceName}`;
            const fetchedSeoData = await seoService.fetchSEOData('space', spaceIdentifier);
            setSeoData(fetchedSeoData);
        } catch (err) {
            // Log the error, but don't set the main page error state.
            // The page can render with fallback SEO data.
            console.error("Failed to fetch SEO data (non-critical):", err);
        }
    }

    fetchPageData();
    fetchSeo();
  }, [city, spaceName, cities, citiesLoaded]);

  useEffect(() => {
    let cancelled = false;

    if (!space?.id || !experimentalFeaturesEnabled) {
      setHasTutors(false);
      return;
    }

    const loadTutors = async () => {
      try {
        const roster = await tutorApiClient.getPublicTutorsBySpaceId(space.id);
        if (!cancelled) setHasTutors(roster.length > 0);
      } catch {
        if (!cancelled) setHasTutors(false);
      }
    };

    loadTutors();
    return () => {
      cancelled = true;
    };
  }, [space?.id, experimentalFeaturesEnabled]);

  useEffect(() => {
    const canPurchasePasses = isPassPurchaseAvailableForRooms(space?.rooms);
    if (!space?.id || !canPurchasePasses) {
      setPassOffers([]);
      return;
    }

    let cancelled = false;

    const loadPassOffers = async () => {
      const rooms = space.rooms ?? [];
      if (rooms.length === 0) {
        setPassOffers([]);
        return;
      }

      setPassOffersLoading(true);
      try {
        const roomIds = rooms.map((room) => room.id).filter(Boolean);
        const offers = await apiClient.listPassOffers({
          targetType: 'ROOM',
          targetIds: roomIds,
        });
        if (!cancelled) setPassOffers(offers);
      } catch (err) {
        console.error('Error fetching pass offers for space rooms:', err);
        if (!cancelled) setPassOffers([]);
      } finally {
        if (!cancelled) setPassOffersLoading(false);
      }
    };

    loadPassOffers();
    return () => {
      cancelled = true;
    };
  }, [space?.id, space?.rooms]);

  const handleBuyPassOffer = (offer: PassOfferSummary) => {
    setSelectedPassOffer(offer);
    setPassDialogOpen(true);
  };

  // Scroll to and highlight room when roomName is in URL, and open booking if /book is present
  useEffect(() => {
    if (!roomName || !space) return;

    const shouldOpenBooking = location.pathname.endsWith('/book');
    if (!shouldOpenBooking) {
      didAutoOpenBookingRef.current = false;
    }

    const matchingRoom = space.rooms.find((room) => urlFriendlyEquals(room.name, roomName));

    if (matchingRoom) {
      setTimeout(() => {
        const roomElement = document.getElementById(`room-card-${toUrlFriendly(matchingRoom.name)}`);
        if (roomElement) {
          roomElement.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 100);

      if (shouldOpenBooking && !didAutoOpenBookingRef.current) {
        const rs = matchingRoom as unknown as RoomSummary;
        if (isRoomBookingAllowed(rs)) {
          didAutoOpenBookingRef.current = true;
          const resume = readRoomBookingResumeFromSearchParams(searchParams);
          const searchTime = location.state?.roomAvailableFromTime ?? resume.roomAvailableFromTime;
          const searchDuration = location.state?.durationInMinutes ?? resume.durationInMinutes;
          let dt: Date | undefined;
          let dur: number | undefined;
          if (searchTime && searchDuration != null) {
            const parsed = new Date(searchTime);
            if (!isNaN(parsed.getTime())) dt = parsed;
            dur = Number(searchDuration);
          } else {
            const now = new Date();
            const minutes = Math.ceil(now.getMinutes() / 30) * 30;
            now.setMinutes(minutes, 0, 0);
            dt = new Date(now.getTime() + 2 * 60 * 60 * 1000);
            dur = 120;
          }
          setSelectedRoom(rs);
          setInitialDateTime(dt);
          setInitialDuration(dur);
          setInitialStepKey(resume.open ? 'whoIsComing' : null);
          trackStartBooking({
            provider_type: 'space',
            provider_id: space.id,
            city: space.city,
            currency: String(rs.currecyCode ?? space.currencyCode ?? 'PLN'),
            booking_value: estimateHourlyBookingValue(Number(rs.pricePerH), dur),
          });
          setBookingOpen(true);
          if (resume.open) {
            setSearchParams(stripRoomBookingResumeSearchParams(searchParams), { replace: true });
          }
        }
      }
    }
  }, [roomName, space, location.pathname, location.state, searchParams, setSearchParams]);

  // Use native body scrolling for better Android/iOS behavior

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !space) {
    return (
      <Typography variant="h6" color="error" align="center" mt={10}>
        {t('spaceSearch.loadError', "Couldn't load space details.")}
      </Typography>
    );
  }

  const fullAddress = [
    space.addressLine1,
    space.addressLine2,
    space.city,
    space.zipCode,
  ]
    .filter(Boolean)
    .join(', ');

  const handleNext = (roomId: string, max: number) => {
    setRoomSteps((prev) => {
      const curr = prev[roomId] || 0;
      return { ...prev, [roomId]: Math.min(curr + 1, max - 1) };
    });
  };
  const handleBack = (roomId: string) => {
    setRoomSteps((prev) => {
      const curr = prev[roomId] || 0;
      return { ...prev, [roomId]: Math.max(curr - 1, 0) };
    });
  };

  const handleCloseGallery = () => {
    setSelectedImageIndex(null);
  };

  const closeRoomGallery = () => setRoomGallery(null);
  const nextRoomGalleryImage = () => {
    if (!roomGallery) return;
    const { room, index } = roomGallery;
    const next = (index + 1) % (room.imageUrls?.length || 1);
    setRoomGallery({ room, index: next });
  };
  const prevRoomGalleryImage = () => {
    if (!roomGallery) return;
    const { room, index } = roomGallery;
    const max = room.imageUrls?.length || 1;
    const prev = (index - 1 + max) % max;
    setRoomGallery({ room, index: prev });
  };

  const handleNextImage = () => {
    if (selectedImageIndex !== null && space) {
      setSelectedImageIndex((selectedImageIndex + 1) % space.imageUrls.length);
    }
  };

  const handlePreviousImage = () => {
    if (selectedImageIndex !== null && space) {
      setSelectedImageIndex((selectedImageIndex - 1 + space.imageUrls.length) % space.imageUrls.length);
    }
  };

  const handleAmenityClick = (
    event: React.MouseEvent<HTMLElement>,
    amenity: { amenityType: string; details: string; pricePerH: number }
  ) => {
    event.stopPropagation();
    const label = getAmenityLabel(amenity.amenityType) || amenity.details;
    const icon = amenityIcons[amenity.amenityType] || <AddIcon fontSize="small" />;
    setPopoverContent({
      icon,
      label,
      details: amenity.details,
      price: amenity.pricePerH,
      currency: space?.currencyCode?.toString(),
    });
    setPopoverAnchor(event.currentTarget);
  };

  const handleGearClick = (
    event: React.MouseEvent<HTMLElement>,
    gear: { gearType: string; details: string; pricePerH: number }
  ) => {
    event.stopPropagation();
    const label = getGearLabel(gear.gearType);
    const icon = gearIcons[gear.gearType] || <AddIcon fontSize="small" />;
    setPopoverContent({
      icon,
      label,
      details: gear.details,
      price: gear.pricePerH,
      currency: space?.currencyCode?.toString(),
    });
    setPopoverAnchor(event.currentTarget);
  };

  const handlePopoverClose = () => {
    setPopoverAnchor(null);
    setPopoverContent(null);
  };

  const handleBackToList = () => {
    // Check if we have a referrer from the same origin (came from within the app)
    const referrer = document.referrer;
    const currentOrigin = window.location.origin;
    
    if (referrer && referrer.startsWith(currentOrigin)) {
      // We came from within the app, go back
      navigate(-1);
    } else {
      // No referrer or external referrer, navigate to search page
      navigate('/user/search');
    }
  };

  const copySpaceLink = () => {
    if (!city || !spaceName) return;
    const spaceUrl = `${window.location.origin}/user/search/${city}/${spaceName}`;
    navigator.clipboard.writeText(spaceUrl);
    setSnackbarMessage(t('common.linkCopied', 'Link copied to clipboard'));
    setSnackbarOpen(true);
    
    // Track share button click
    if (space?.id) {
      apiClient.trackEvent('Space Shared', {
        spaceName: space.name,
      });
    }
  };

  const copyRoomLink = (room: RoomSummary) => {
    if (!city || !spaceName) return;
    const roomUrlName = toUrlFriendly(room.name);
    const roomUrl = `${window.location.origin}/user/search/${city}/${spaceName}/${roomUrlName}`;
    navigator.clipboard.writeText(roomUrl);
    setSnackbarMessage(t('common.linkCopied', 'Link copied to clipboard'));
    setSnackbarOpen(true);
    
    // Track share button click
    if (space?.id) {
      apiClient.trackEvent('Room Shared', {
        spaceName: space.name,
        roomId: room.id,
        roomName: room.name,
      });
    }
  };

  const marketingCanonical = resolveMarketingCanonicalForAppPath(city, spaceName);

  return (
    <>
      <SEO
        title={seoData?.title || space?.name}
        description={seoData?.description || space?.description}
        keywords={seoData?.keywords}
        image={seoData?.image || (space?.imageUrls && space.imageUrls.length > 0 ? space.imageUrls[0] : undefined)}
        url={marketingCanonical || seoData?.url}
        canonical={marketingCanonical}
        type={seoData?.type || 'website'}
        structuredData={seoData?.structuredData}
      />
      {/* Match SearchPage scroll behavior */}
      <GlobalStyles styles={{
        // Align with SearchPage
        '.gm-ui-hover-effect': {
          display: 'none !important',
        },
        '.gm-style .gm-style-iw, .gm-style .gm-style-iw-d, .gm-style .gm-style-iw-c': {
          background: '#1F2129 !important',
          boxShadow: 'none !important',
          border: 'none !important',
          padding: '0 !important',
          borderRadius: '12px !important',
          maxWidth: 'none !important',
          overflow: 'hidden !important',
        },
        '.gm-style .gm-style-iw, .gm-style .gm-style-iw-d': {
          maxWidth: 'none !important',
          overflow: 'hidden !important',
        },
        '.gm-style .gm-style-iw-d::-webkit-scrollbar': {
          display: 'none !important',
        },
        '.gm-style .gm-style-iw-t, .gm-style .gm-style-iw-tc, .gm-style .gm-style-iw-ch': {
          background: '#1F2129 !important',
          boxShadow: 'none !important',
          border: 'none !important',
        },
        '.gm-style .gm-style-iw-t::after, .gm-style .gm-style-iw-tc::after, .gm-style .gm-style-iw-ch::after': {
          background: '#1F2129 !important',
          boxShadow: 'none !important',
          border: 'none !important',
        },
        html: { scrollBehavior: 'auto', overflowAnchor: 'none' },
        body: { overflowAnchor: 'none' },
      }} />
      <Box className="space-result-page" sx={{ 
        width: '100%',
        maxWidth: 1400,
        mx: 'auto', 
        px: 2,
        pt: { xs: 2, sm: 4 },
        pb: { xs: 2, md: 6 },
        mb: 0
      }}>
        {/* Back (same style as room page) */}
        <Chip
          label={`← ${t('common.backToList', 'Back to list')}`}
          onClick={handleBackToList}
          sx={{ 
            mb: { xs: 1, sm: 2 },
            fontSize: { xs: '0.75rem', sm: '0.875rem' },
            bgcolor: 'transparent',
            color: 'text.primary',
            border: '1px solid',
            borderColor: 'divider',
            '&:hover': {
              bgcolor: 'transparent'
            }
          }}
        />
        

        {/* Gallery Dialog */}
        <Dialog
          open={selectedImageIndex !== null}
          onClose={handleCloseGallery}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent sx={{ p: 0, bgcolor: 'black', position: 'relative' }}>
                          {selectedImageIndex !== null && space && (
                <>
                  {isVideoUrl(space.imageUrls[selectedImageIndex]) ? (
                    <VideoPlayer
                      src={space.imageUrls[selectedImageIndex]}
                      alt="Gallery view"
                      sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '90vh',
                      }}
                    />
                  ) : (
                    <MediaDisplay
                      src={space.imageUrls[selectedImageIndex]}
                      alt="Gallery view"
                      showPlayButton={false}
                      sx={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '90vh',
                        objectFit: 'contain',
                      }}
                    />
                  )}
                <IconButton
                  onClick={handlePreviousImage}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                  onClick={handleNextImage}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.9)',
                    },
                  }}
                >
                  <KeyboardArrowRight />
                </IconButton>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Room Gallery Dialog */}
        <Dialog
          open={Boolean(roomGallery)}
          onClose={closeRoomGallery}
          maxWidth="lg"
          fullWidth
        >
          <DialogContent sx={{ p: 0, bgcolor: 'black', position: 'relative' }}>
            {roomGallery && (
              <>
                {isVideoUrl(roomGallery.room.imageUrls[roomGallery.index]) ? (
                  <VideoPlayer
                    src={roomGallery.room.imageUrls[roomGallery.index]}
                    alt={`${roomGallery.room.name} - Gallery view`}
                    sx={{ width: '100%', height: 'auto', maxHeight: '90vh' }}
                  />
                ) : (
                  <MediaDisplay
                    src={roomGallery.room.imageUrls[roomGallery.index]}
                    alt={`${roomGallery.room.name} - Gallery view`}
                    showPlayButton={false}
                    sx={{ width: '100%', height: 'auto', maxHeight: '90vh', objectFit: 'contain' }}
                  />
                )}
                <IconButton
                  onClick={prevRoomGalleryImage}
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                  }}
                >
                  <KeyboardArrowLeft />
                </IconButton>
                <IconButton
                  onClick={nextRoomGalleryImage}
                  sx={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    '&:hover': { bgcolor: 'rgba(255, 255, 255, 0.9)' },
                  }}
                >
                  <KeyboardArrowRight />
                </IconButton>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Main Content Layout */}
        <Grid container spacing={2} alignItems="stretch">
          {/* Left Column - Header + Rooms */}
          <Grid item xs={12} md={8}>
            {/* Space Info Header (constrained to left column) */}
            <Box sx={{ mb: { xs: 2, sm: 4 } }}>
              <Grid container spacing={{ xs: 2, sm: 3 }} alignItems="flex-start">
                {/* Logo */}
                <Grid item xs="auto">
                  <Box
                    sx={{
                      width: { xs: 96, sm: 120 },
                      height: { xs: 96, sm: 120 },
                      bgcolor: 'white',
                      borderRadius: 1,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      border: '1px solid',
                      borderColor: 'divider',
                      flexShrink: 0
                    }}
                  >
                    {space.imageUrls && space.imageUrls.length > 0 ? (
                      <MediaDisplay
                        src={space.imageUrls[0]}
                        alt={space.name}
                        onClick={() => setSelectedImageIndex(0)}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          cursor: 'pointer',
                          borderRadius: 1,
                        }}
                      />
                    ) : null}
                  </Box>
                </Grid>

                {/* Space Details */}
                <Grid item xs>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                    <Typography variant="h4" sx={{ 
                      fontSize: { xs: '1.25rem', sm: '2rem' },
                      fontWeight: 'bold',
                      color: 'white',
                      flex: 1
                    }}>
                      {space.name}
                    </Typography>
                    {isSpace24hOpen(space.weeklySchedule) && (
                      <Chip
                        icon={<MusicNoteIcon sx={{ fontSize: '0.75rem !important', color: 'inherit' }} />}
                        label={t('space.open24_7', '24/7 Open')}
                        size="small"
                        sx={{
                          height: 24,
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: (theme) => theme.palette.success.main,
                          color: 'white',
                          '& .MuiChip-icon': { color: 'inherit' },
                          '& .MuiChip-label': {
                            px: 1,
                          },
                        }}
                      />
                    )}
                    <IconButton
                      onClick={(e) => {
                        e.stopPropagation();
                        copySpaceLink();
                      }}
                      size="small"
                      sx={{
                        color: 'grey.400',
                        '&:hover': {
                          color: 'primary.main',
                          bgcolor: 'rgba(79, 95, 235, 0.1)'
                        },
                        transition: 'all 0.2s ease'
                      }}
                    >
                      <ShareIcon fontSize="small" />
                    </IconButton>
                    {experimentalFeaturesEnabled && hasTutors && space && (
                      <Button
                        component={RouterLink}
                        to={schoolTutorPagePath(space.city, space.name)}
                        size="small"
                        variant="outlined"
                        endIcon={<OpenInNewRoundedIcon />}
                        sx={{ ml: { xs: 0, sm: 0.5 } }}
                      >
                        {t('schoolPage.viewTutors', 'View tutors')}
                      </Button>
                    )}
                  </Box>
                  <Typography variant="body1" sx={{ 
                    fontSize: { xs: '0.85rem', sm: '1rem' },
                    mb: 1,
                    color: 'grey.300',
                    whiteSpace: 'pre-line'
                  }}>
                    {space.description}
                  </Typography>
                  


                  {/* Space Amenities */}
                  {space.amenities?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        color: 'grey.300',
                        mb: 0.5,
                        fontWeight: 'medium'
                      }}>
                        {t('space.amenities', 'Amenities')}:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {space.amenities.map((amenity) => {
                          const key = amenity.amenityType;
                          const label = getAmenityLabel(key) || amenity.details;
                          const icon = amenityIcons[key] || <AddIcon fontSize="small" />;
                          return (
                            <Chip 
                              key={key + amenity.details} 
                              icon={icon}
                              label={label} 
                              size="small"
                              onClick={(e) => handleAmenityClick(e, amenity)}
                              sx={{ 
                                height: 20,
                                bgcolor: 'grey.700',
                                color: 'white',
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'grey.600',
                                },
                                '& .MuiChip-label': {
                                  fontSize: '0.65rem',
                                  px: 0.5
                                },
                                '& .MuiChip-icon': {
                                  fontSize: '0.75rem',
                                  color: 'grey.300'
                                }
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  )}

                  {/* Space Gears */}
                  {space.gears?.length > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        color: 'grey.300',
                        mb: 0.5,
                        fontWeight: 'medium'
                      }}>
                        {t('space.gears', 'Gears')}:
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {space.gears.map((gear, idx) => {
                          const key = gear.gearType;
                          const label = getGearLabel(key);
                          const icon = gearIcons[key] || <AddIcon fontSize="small" />;
                          return (
                            <Chip 
                              key={`${key}-${idx}`} 
                              icon={icon}
                              label={label} 
                              size="small"
                              onClick={(e) => handleGearClick(e, gear)}
                              sx={{ 
                                height: 20,
                                bgcolor: 'grey.700',
                                color: 'white',
                                cursor: 'pointer',
                                '&:hover': {
                                  bgcolor: 'grey.600',
                                },
                                '& .MuiChip-label': {
                                  fontSize: '0.65rem',
                                  px: 0.5
                                },
                                '& .MuiChip-icon': {
                                  fontSize: '0.75rem',
                                  color: 'grey.300'
                                }
                              }}
                            />
                          );
                        })}
                      </Stack>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </Box>

            <PassCheckoutResumeErrorAlert />
            {isPassPurchaseAvailableForRooms(space.rooms) &&
              (passOffersLoading || passOffers.length > 0) && (
              <Box sx={{ mb: { xs: 2, sm: 3 } }}>
                <PassOfferList
                  offers={passOffers}
                  loading={passOffersLoading}
                  onBuy={handleBuyPassOffer}
                />
              </Box>
            )}
        {/* Rooms as Cards with Amenities, Gear & Pricing */}
        <Grid container spacing={2}>
          {space.rooms.map((room) => {
            const step = roomSteps[room.id] || 0;
            const maxSteps = room.imageUrls.length;
            const roomSummary = room as unknown as RoomSummary;
            const bookable = isRoomBookingAllowed(roomSummary);
            const bookingBlockedHintText = !bookable
              ? roomBookingBlockedHint(t, roomSummary.bookingAccess?.bookingBlockedReason ?? null)
              : null;

            const openRoomGallery = () => {
              setRoomGallery({ room: room as unknown as RoomSummary, index: step });
            };

            const openBooking = () => {
              // prefer values from SearchPage state if present
              const searchTime = location.state?.roomAvailableFromTime;
              const searchDuration = location.state?.durationInMinutes;
              let dt: Date | undefined;
              let dur: number | undefined;
              if (searchTime && searchDuration) {
                const parsed = new Date(searchTime);
                if (!isNaN(parsed.getTime())) dt = parsed;
                dur = Number(searchDuration);
              } else {
                const now = new Date();
                const minutes = Math.ceil(now.getMinutes() / 30) * 30;
                now.setMinutes(minutes, 0, 0);
                dt = new Date(now.getTime() + 2 * 60 * 60 * 1000);
                dur = 120;
              }
              if (!isRoomBookingAllowed(room as unknown as RoomSummary)) return;
              setSelectedRoom(room as unknown as RoomSummary);
              setInitialDateTime(dt);
              setInitialDuration(dur);
              trackStartBooking({
                provider_type: 'space',
                provider_id: space.id,
                city: space.city,
                currency: String(roomSummary.currecyCode ?? space.currencyCode ?? 'PLN'),
                booking_value: estimateHourlyBookingValue(Number(roomSummary.pricePerH), dur),
              });
              setBookingOpen(true);
            };

            // Check if this room should be highlighted
            const roomUrlName = toUrlFriendly(room.name);
            const isHighlighted = roomName && roomUrlName === roomName;
            const hasHighlightedRoom = !!roomName; // Check if any room is highlighted

            return (
                <Grid item xs={12} key={room.id}>
                    <Card 
                      id={`room-card-${roomUrlName}`}
                      onClick={bookable ? openBooking : undefined}
                      sx={{ 
                        boxSizing: 'border-box',
                        width: '100%',
                        height: 'auto',
                        background: isHighlighted ? '#1E213A' : 'rgba(255, 255, 255, 0.04)',
                        border: isHighlighted ? '1px solid' : 'none',
                        borderColor: isHighlighted ? 'primary.main' : 'transparent',
                        borderRadius: '18.1385px',
                        flex: 'none',
                        order: 0,
                        alignSelf: 'stretch',
                        flexGrow: 0,
                        overflow: 'hidden',
                        transition: 'box-shadow 0.2s ease, border-color 0.2s ease, background 0.2s ease',
                        boxShadow: isHighlighted
                          ? '0 6px 18px rgba(0,0,0,0.45)'
                          : 'none',
                        position: 'relative',
                        cursor: bookable ? 'pointer' : 'default',
                        // Only show hover effects if no room is highlighted, or if this is the highlighted room
                        '&:hover': !bookable || (hasHighlightedRoom && !isHighlighted) ? {} : {
                          boxShadow: isHighlighted 
                            ? '0 6px 18px rgba(0,0,0,0.45)' 
                            : '0 6px 18px rgba(0,0,0,0.35)',
                          border: '1px solid #4F5FEB',
                          background: '#1E213A'
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, position: 'relative' }}>
                        {/* Image Section */}
                    <Box sx={{ 
                      position: 'relative', 
                          width: { xs: '100%', sm: 340 }, 
                          height: { xs: 200, sm: 320 },
                      flexShrink: 0,
                      overflow: 'hidden',
                      zIndex: 0
                    }} onClick={(e) => { e.stopPropagation(); openRoomGallery(); }}>
                      <MediaDisplay
                        src={room.imageUrls[step] || '/logo512.png'}
                        alt={room.name}
                        sx={{ 
                          width: '100%', 
                              height: '100%',
                              cursor: 'pointer'
                            }}
                          />
                          
                          
                          {/* Navigation arrows */}
                          {maxSteps > 1 && (
                            <>
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleBack(room.id);
                            }}
                            disabled={step === 0}
                            sx={{
                              position: 'absolute',
                              left: 8,
                              top: '50%',
                              transform: 'translateY(-50%)',
                              bgcolor: 'rgba(255, 255, 255, 0.8)',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.9)',
                              },
                            }}
                          >
                            <KeyboardArrowLeft />
                          </IconButton>
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNext(room.id, maxSteps);
                                }}
                                disabled={step === maxSteps - 1}
                        sx={{
                                  position: 'absolute',
                                  right: 8,
                                  top: '50%',
                                  transform: 'translateY(-50%)',
                                  bgcolor: 'rgba(255, 255, 255, 0.8)',
                                  '&:hover': {
                                    bgcolor: 'rgba(255, 255, 255, 0.9)',
                                  },
                                }}
                              >
                                <KeyboardArrowRight />
                              </IconButton>
                            </>
                          )}
                          {/* Dots indicator */}
                          {maxSteps > 1 && (
                            <Box sx={{
                          position: 'absolute',
                          bottom: 8,
                              left: '50%',
                              transform: 'translateX(-50%)',
                              display: 'flex',
                              gap: 0.5
                            }}>
                              {Array.from({ length: maxSteps }).map((_, index) => (
                                <Box
                                  key={index}
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: index === step ? 'white' : 'rgba(255, 255, 255, 0.5)'
                                  }}
                                />
                              ))}
                            </Box>
                          )}
                    </Box>

                        {/* Content Section */}
                      <Box sx={{ 
                        flex: 1,
                        p: 3,
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: { xs: 'auto', sm: 260 },
                        position: 'relative',
                        zIndex: 1
                      }}>
                        <Box sx={{ 
                          display: 'flex', 
                          alignItems: 'flex-start', 
                          justifyContent: 'space-between',
                          gap: 1,
                          mb: 1,
                          minWidth: 0
                        }}>
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
                            <Typography variant="h6" component="span" sx={{ 
                              fontSize: { xs: '1.125rem', sm: '1.5rem' },
                              color: 'white',
                              fontWeight: 'bold',
                              lineHeight: 1.2,
                              whiteSpace: 'normal',
                              wordBreak: 'break-word',
                              display: 'inline'
                            }}>
                              {room.name}
                            </Typography>
                            <IconButton
                              onClick={(e: React.MouseEvent) => {
                                e.stopPropagation();
                                copyRoomLink(room as unknown as RoomSummary);
                              }}
                              size="small"
                              sx={{
                                color: 'grey.400',
                                display: 'inline-flex',
                                verticalAlign: 'middle',
                                ml: 0.5,
                                '&:hover': {
                                  color: 'primary.main',
                                  bgcolor: 'rgba(79, 95, 235, 0.1)'
                                },
                                transition: 'all 0.2s ease',
                                p: 0.5
                              }}
                            >
                              <ShareIcon fontSize="small" />
                            </IconButton>
                          </Box>
                          <Stack direction="column" alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0, ml: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                              <Typography variant="h6" sx={{ 
                                fontSize: { xs: '0.95rem', sm: '1rem' },
                                color: 'grey.300',
                                fontWeight: 'bold'
                              }}>
                                {new Intl.NumberFormat(undefined, {
                                  style: 'currency',
                                  currency: space?.currencyCode?.toString(),
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 2,
                                }).format(room.pricePerH)}/h
                              </Typography>
                              {bookable ? (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); openBooking(); }}
                                  sx={{ 
                                    whiteSpace: 'nowrap',
                                    display: { xs: 'none', sm: 'inline-flex' }
                                  }}
                                >
                                  {t('spaceCalendar.book','REZERWUJ')}
                                </Button>
                              ) : (
                                <Tooltip title={bookingBlockedHintText || ''}>
                                  <span>
                                    <Button
                                      variant="outlined"
                                      size="small"
                                      disabled
                                      onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
                                      sx={{ 
                                        whiteSpace: 'nowrap',
                                        display: { xs: 'none', sm: 'inline-flex' }
                                      }}
                                    >
                                      {t('spaceCalendar.book','REZERWUJ')}
                                    </Button>
                                  </span>
                                </Tooltip>
                              )}
                            </Box>
                            {!bookable && bookingBlockedHintText && (
                              <Typography
                                variant="caption"
                                color="error"
                                sx={{ display: { xs: 'none', sm: 'block' }, textAlign: 'right', maxWidth: 280 }}
                              >
                                {bookingBlockedHintText}
                              </Typography>
                            )}
                          </Stack>
                        </Box>

                        {/* Room Categories */}
                        {room.categories && room.categories.length > 0 && (
                          <Box sx={{ mb: 1.5 }}>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                              {[...room.categories]
                                .sort((a, b) => {
                                  // Primary category first
                                  if (a === room.primaryCategory) return -1;
                                  if (b === room.primaryCategory) return 1;
                                  return 0;
                                })
                                .map((category) => {
                                  const isPrimary = room.primaryCategory === category;
                                  const label = getCategoryLabel(category);
                                  
                                  // Primary = purple (#4F5FEB), others = teal (#58a7b9)
                                  const primaryColor = '#4F5FEB';
                                  const secondaryColor = '#58a7b9';
                                  const color = isPrimary ? primaryColor : secondaryColor;
                                  
                                  return (
                                    <Box
                                      key={category}
                                      sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        px: 2,
                                        py: 0.75,
                                        bgcolor: color,
                                        color: 'white',
                                        borderRadius: '20px',
                                        fontSize: '0.75rem',
                                        fontWeight: isPrimary ? 700 : 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.5px',
                                        boxShadow: `0 2px 4px ${color}40`,
                                        border: 'none',
                                      }}
                                    >
                                      {label}
                                    </Box>
                                  );
                                })}
                            </Stack>
                          </Box>
                        )}

                        {/* Room Size */}
                        {room.area && room.areaUnit && (
                          <Typography variant="body2" sx={{ 
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            color: 'grey.400',
                            mb: 1,
                            fontWeight: 500
                          }}>
                            {t('room.size', 'Size')}: {room.area} {room.areaUnit}
                          </Typography>
                        )}
                            
                        {/* Dashed Line Separator */}
                        <Box sx={{ 
                          width: '100%', 
                          height: '1px', 
                          borderTop: '1px dashed rgba(255, 255, 255, 0.2)',
                          mb: 2
                        }} />

                        <Typography 
                          variant="body2" 
                          sx={{ 
                            fontSize: { xs: '0.85rem', sm: '0.875rem' },
                            mb: 2,
                            color: 'grey.300',
                            lineHeight: 1.4,
                            display: '-webkit-box',
                            WebkitLineClamp: { xs: 3, sm: 'unset' },
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            whiteSpace: 'pre-line'
                          }}
                        >
                          {room.description}
                        </Typography>

                        {/* Dashed Line Separator */}
                        <Box sx={{ 
                          width: '100%', 
                          height: '1px', 
                          borderTop: '1px dashed rgba(255, 255, 255, 0.2)',
                          mb: 2
                        }} />

                        {/* Equipment Chips */}
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                          {room.gears.map((gear, idx) => {
                            const icon = gearIcons[gear.gearType] || <AddIcon fontSize="small" />;
                            return (
                              <Chip
                                key={`${gear.gearType}-${idx}`}
                                icon={icon}
                                label={getGearLabel(gear.gearType)}
                                size="small"
                                onClick={(e) => handleGearClick(e, gear)}
                                sx={{ 
                                  height: 20,
                                  bgcolor: 'grey.700',
                                  color: 'white',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    bgcolor: 'grey.600',
                                  },
                                  '& .MuiChip-label': {
                                    fontSize: '0.65rem',
                                    px: 0.5
                                  },
                                  '& .MuiChip-icon': {
                                    fontSize: '0.75rem',
                                    color: 'grey.300'
                                  }
                                }}
                              />
                            );
                          })}
                        </Stack>

                        {/* Dashed Line Separator */}
                        <Box sx={{ 
                          width: '100%', 
                          height: '1px', 
                          borderTop: '1px dashed rgba(255, 255, 255, 0.2)',
                          my: 2
                        }} />

                        {/* Room Amenities */}
                        {room.amenities && room.amenities.length > 0 && (
                          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                            {room.amenities.map((amenity, index) => {
                              const key = amenity.amenityType;
                              const label = getAmenityLabel(key) || amenity.details;
                              const icon = amenityIcons[key] || <AddIcon fontSize="small" />;
                              return (
                                <Chip
                                  key={`${key}-${amenity.details}-${index}`}
                                  icon={icon}
                                  label={label}
                                  size="small"
                                  onClick={(e) => handleAmenityClick(e, amenity)}
                                  sx={{
                                    height: 20,
                                    bgcolor: 'grey.700',
                                    color: 'white',
                                    cursor: 'pointer',
                                    '&:hover': {
                                      bgcolor: 'grey.600',
                                    },
                                    '& .MuiChip-label': {
                                      fontSize: '0.65rem',
                                      px: 0.5
                                    },
                                    '& .MuiChip-icon': {
                                      fontSize: '0.75rem',
                                      color: 'grey.300'
                                    }
                                  }}
                                />
                              );
                            })}
                          </Stack>
                        )}

                        {/* Mobile Book Button - appears below amenities */}
                        <Box sx={{ display: { xs: 'block', sm: 'none' }, mt: 2, width: '100%' }}>
                          {/* Dashed Line Separator */}
                          <Box sx={{ 
                            width: '100%', 
                            height: '1px', 
                            borderTop: '1px dashed rgba(255, 255, 255, 0.2)',
                            mb: 2
                          }} />
                          {bookable ? (
                            <Button
                              variant="outlined"
                              fullWidth
                              onClick={(e: React.MouseEvent) => { e.stopPropagation(); openBooking(); }}
                              sx={{ whiteSpace: 'nowrap' }}
                            >
                              {t('spaceCalendar.book','REZERWUJ')}
                            </Button>
                          ) : (
                            <Tooltip title={bookingBlockedHintText || ''}>
                              <span style={{ width: '100%', display: 'block' }}>
                                <Button
                                  variant="outlined"
                                  fullWidth
                                  disabled
                                  onClick={(e: React.MouseEvent) => { e.stopPropagation(); }}
                                  sx={{ whiteSpace: 'nowrap' }}
                                >
                                  {t('spaceCalendar.book','REZERWUJ')}
                                </Button>
                              </span>
                            </Tooltip>
                          )}
                          {!bookable && bookingBlockedHintText && (
                            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                              {bookingBlockedHintText}
                            </Typography>
                          )}
                        </Box>

                        {/* Bottom action removed - header contains compact booking button */}

                      </Box>
                    </Box>
                  </Card>
              </Grid>
            );
          })}
        </Grid>
          </Grid>

          {/* Right Column - Sidebar */}
          <Grid item xs={12} md={4} sx={{ mt: { xs: 1.5, md: -3 } }}>
            {/* Location Bar */}
            <Box sx={{ 
              bgcolor: 'transparent',
              borderRadius: 2,
              mb: 2,
              overflow: 'hidden'
            }}>
                <Box sx={{ p: 2 }}>
                <Typography variant="h6" sx={{ 
                  fontSize: '1.1rem',
                  mb: 2,
                  color: 'white',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: 0.5
                }}>
                  {t('space.location', 'LOCATION')}
                </Typography>
                
                {/* Google Map */}
                <Box sx={{
                  width: '100%',
                  height: { xs: 160, md: 180 },
                  borderRadius: 1,
                  mb: 1.5,
                  overflow: 'hidden',
                  position: 'relative'
                }}>
                  {loadError && (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <Typography color="error">Error loading maps</Typography>
                    </Box>
                  )}
                  {!isLoaded ? (
                    <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                      <CircularProgress />
                    </Box>
                  ) : space?.latitude && space?.longitude ? (
                    <GoogleMap
                      mapContainerStyle={mapContainerStyle}
                      center={{
                        lat: space.latitude,
                        lng: space.longitude,
                      }}
                      zoom={15}
                      options={{
                        gestureHandling: 'greedy',
                        fullscreenControl: false,
                        streetViewControl: false,
                        mapTypeControl: false,
                        zoomControl: true,
                        clickableIcons: false,
                        styles: [
                          {
                            featureType: 'administrative.locality',
                            elementType: 'labels.text',
                            stylers: [{ visibility: 'off' }]
                          },
                          {
                            featureType: 'administrative.neighborhood',
                            elementType: 'labels.text',
                            stylers: [{ visibility: 'off' }]
                          }
                        ]
                      }}
                    >
                      <Marker
                        position={{
                          lat: space.latitude,
                          lng: space.longitude,
                        }}
                        onClick={() => window.open(space.googleMapsUrl, '_blank')}
                      />
                    </GoogleMap>
                  ) : (
                    <Box sx={{
                      width: '100%',
                      height: '100%',
                      bgcolor: 'grey.700',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Typography variant="body2" sx={{ color: 'grey.400' }}>
                        No location data available
                      </Typography>
                    </Box>
                  )}
                </Box>
                
                {/* Address Bar */}
                <Box 
                  onClick={() => window.open(space.googleMapsUrl, '_blank')}
                  sx={{ 
                    display: 'flex', 
                    alignItems: 'flex-start', 
                    gap: 1,
                    bgcolor: 'background.paper',
                    p: 1.5,
                    borderRadius: 1,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.08)',
                      borderColor: 'primary.main',
                      transform: 'translateY(-1px)'
                    }
                  }}
                >
                  <LocationOnIcon sx={{ color: 'primary.main', fontSize: 20 }} />
                  <Typography variant="body2" sx={{ 
                    color: 'white',
                    fontSize: '0.875rem',
                    whiteSpace: 'normal',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                    lineHeight: 1.3,
                    maxHeight: '2.6em'
                  }}>
                    {space?.formattedAddress || fullAddress}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* How to Find the Space Section */}
            {space.howToFindVideoUrls && space.howToFindVideoUrls.length > 0 && (
              <Box 
                ref={howToFindRef}
                sx={{ 
                  bgcolor: 'transparent',
                  borderRadius: 2,
                  mb: 2,
                  overflow: 'hidden',
                  scrollMarginTop: '20px'
                }}
              >
                <Box sx={{ p: 2 }}>
                  <Typography variant="h6" sx={{ 
                    fontSize: '1.1rem',
                    mb: 2,
                    color: 'white',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    {t('space.howToFind', 'HOW TO FIND THE SPACE')}
                  </Typography>
                  
                  {/* Media Display */}
                  <Box 
                    sx={{ 
                      borderRadius: 1,
                      overflow: 'hidden',
                      bgcolor: 'black',
                      border: '1px solid',
                      borderColor: 'divider',
                    }}
                  >
                    {isVideoUrl(space.howToFindVideoUrls[0]) ? (
                      <VideoPlayer
                        src={space.howToFindVideoUrls[0]}
                        alt={`How to find ${space.name}`}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: { xs: 200, md: 300 },
                        }}
                        showControls={true}
                      />
                    ) : (
                      <MediaDisplay
                        src={space.howToFindVideoUrls[0]}
                        alt={`How to find ${space.name}`}
                        sx={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: { xs: 200, md: 300 },
                          objectFit: 'contain',
                        }}
                      />
                    )}
                  </Box>
                </Box>
              </Box>
            )}

            {/* Contact Information Bar */}
            {space.contacts && space.contacts.filter(c => c.visible).length > 0 && (
              <Box sx={{ 
                bgcolor: 'rgba(49, 51, 59, 0.6)',
                borderRadius: 3,
                mb: 2,
                overflow: 'hidden',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.1)'
              }}>
                <Box sx={{ p: 3 }}>
                  <Typography variant="h6" sx={{ 
                    fontSize: '1.1rem',
                    mb: 2.5,
                    color: 'white',
                    fontWeight: 'bold',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5
                  }}>
                    {t('space.contact', 'CONTACT')}
                  </Typography>
                  
                  {/* Organization Contacts */}
                  <Stack spacing={1.5}>
                    {space.contacts
                      .filter(contact => contact.visible)
                      .map((contact, index) => {
                        const getIcon = () => {
                          switch (contact.type) {
                            case ContactType.PHONE_NUMBER:
                              return <PhoneIcon sx={{ fontSize: 22 }} />;
                            case ContactType.EMAIL:
                              return <EmailIcon sx={{ fontSize: 22 }} />;
                            case ContactType.INSTAGRAM:
                              return <InstagramIcon sx={{ fontSize: 22 }} />;
                            case ContactType.FACEBOOK:
                              return <FacebookIcon sx={{ fontSize: 22 }} />;
                            case ContactType.TELEGRAM:
                              return <SendIcon sx={{ fontSize: 22 }} />;
                            case ContactType.LINKTREE:
                              return <LinkIcon sx={{ fontSize: 22 }} />;
                            case ContactType.WEBSITE:
                              return <LanguageIcon sx={{ fontSize: 22 }} />;
                            default:
                              return <LinkIcon sx={{ fontSize: 22 }} />;
                          }
                        };

                        const toExternalUrl = (value: string) =>
                          value.startsWith('http') ? value : `https://${value.replace(/^\/+/, '')}`;

                        const getHref = () => {
                          switch (contact.type) {
                            case ContactType.PHONE_NUMBER:
                              return `tel:${contact.value}`;
                            case ContactType.EMAIL:
                              return `mailto:${contact.value}`;
                            case ContactType.INSTAGRAM:
                              if (contact.value.startsWith('http')) {
                                return contact.value;
                              }
                              // Handle @username format
                              const instagramUsername = contact.value.startsWith('@') 
                                ? contact.value.substring(1) 
                                : contact.value;
                              return `https://instagram.com/${instagramUsername}`;
                            case ContactType.FACEBOOK:
                              return contact.value.startsWith('http') ? contact.value : `https://facebook.com/${contact.value}`;
                            case ContactType.TELEGRAM:
                              if (contact.value.startsWith('http')) {
                                return contact.value;
                              }
                              // Handle @username format
                              const telegramUsername = contact.value.startsWith('@') 
                                ? contact.value.substring(1) 
                                : contact.value;
                              return `https://t.me/${telegramUsername}`;
                            case ContactType.LINKTREE:
                            case ContactType.WEBSITE:
                              return toExternalUrl(contact.value);
                            default:
                              return toExternalUrl(contact.value);
                          }
                        };

                        const opensInSameTab =
                          contact.type === ContactType.PHONE_NUMBER || contact.type === ContactType.EMAIL;

                        return (
                          <Box
                            key={index}
                            component="a"
                            href={getHref()}
                            target={opensInSameTab ? '_self' : '_blank'}
                            rel={opensInSameTab ? '' : 'noopener noreferrer'}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 2,
                              p: 1.5,
                              borderRadius: 1.5,
                              color: 'rgba(255, 255, 255, 0.95)',
                              textDecoration: 'none',
                              transition: 'all 0.25s ease',
                              bgcolor: 'rgba(255, 255, 255, 0.03)',
                              border: '1px solid rgba(255, 255, 255, 0.08)',
                              '&:hover': {
                                bgcolor: 'rgba(255, 255, 255, 0.08)',
                                borderColor: 'primary.main',
                                transform: 'translateX(4px)',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
                              }
                            }}
                          >
                            <Box sx={{ 
                              color: 'primary.main', 
                              display: 'flex', 
                              alignItems: 'center',
                              flexShrink: 0
                            }}>
                              {getIcon()}
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography sx={{ 
                                color: 'inherit', 
                                fontWeight: 500,
                                fontSize: '0.95rem',
                                lineHeight: 1.4,
                                wordBreak: 'break-word'
                              }}>
                                {contact.value}
                              </Typography>
                              {contact.comment && (
                                <Typography sx={{ 
                                  color: 'rgba(255, 255, 255, 0.75)', 
                                  fontSize: '0.875rem', 
                                  mt: 0.5,
                                  lineHeight: 1.4,
                                  fontStyle: 'italic'
                                }}>
                                  {contact.comment}
                                </Typography>
                              )}
                  </Box>
                          </Box>
                        );
                      })}
                  </Stack>
                </Box>
              </Box>
            )}

            {/* Reviews Bar */}
        {(reviewStats || reviews.length > 0) && (
          <Box sx={{ 
                bgcolor: 'rgba(49, 51, 59, 0.6)',
                borderRadius: 3,
                overflow: 'hidden'
              }}>
                <Box sx={{ p: 3 }}>
                  {/* Header with title and rating */}
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                    <Typography variant="h6" sx={{ 
                      fontSize: '1rem',
                      color: 'white',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: 1
                    }}>
                      {t('spaceSearch.reviews', 'REVIEWS')} <Box component="span" sx={{ color: 'rgba(255, 255, 255, 0.4)', fontWeight: 400 }}>{reviewStats?.reviewCount || reviews.length}</Box>
                    </Typography>
                    {reviewStats && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="h5" sx={{ 
                          color: 'white',
                          fontSize: '1.5rem',
                          fontWeight: 700
                        }}>
                          {reviewStats.averageRating.toFixed(1)}
            </Typography>
                        <Box sx={{ display: 'flex', gap: 0.25 }}>
                          {Array.from({ length: 5 }).map((_, index) => (
                            <Box
                              key={index}
                              sx={{
                                width: 20,
                                height: 20,
                                bgcolor: index < Math.floor(reviewStats.averageRating) 
                                  ? '#D4AF37' 
                                  : index < reviewStats.averageRating 
                                    ? '#D4AF37' 
                                    : '#3A3D47',
                                clipPath: 'polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)'
                              }}
                            />
                          ))}
                        </Box>
                      </Box>
                    )}
                  </Box>
                  
                  {/* Individual Reviews */}
                  {(showAllReviews ? reviews : reviews.slice(0, 3)).map((review, index, arr) => (
                    <Box 
                      key={index} 
                      sx={{ 
                        mb: index < arr.length - 1 ? 3 : 0,
                        pb: index < arr.length - 1 ? 3 : 0,
                        borderBottom: index < arr.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography sx={{ 
                          color: 'white',
                          fontSize: '0.875rem',
                          fontWeight: 600
                        }}>
                          {review.username || 'Anonymous'}
                        </Typography>
                        <Typography sx={{ 
                          color: 'rgba(255, 255, 255, 0.6)',
                          fontSize: '0.75rem'
                        }}>
                          {new Date(review.createdAt).toLocaleDateString('en-US', { 
                            month: 'short', 
                            day: 'numeric', 
                            year: 'numeric' 
                          })}
                        </Typography>
                      </Box>
                      <Box sx={{ mb: 1 }}>
                        <Rating
                          value={review.rating}
                          readOnly
                          size="small"
                          precision={0.1}
                          sx={{ color: '#F3AF23' }}
                        />
                      </Box>
                      <Typography sx={{ 
                        color: 'rgba(255, 255, 255, 0.8)',
                        fontSize: '0.875rem',
                        lineHeight: 1.5
                      }}>
                        {review.comment}
                      </Typography>
                    </Box>
                  ))}

                  {/* See all reviews button */}
                  {reviews.length > 3 && (
                    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowAllReviews(!showAllReviews)}
                        sx={{
                          color: 'white',
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                          textTransform: 'none',
                          fontSize: '0.875rem',
                          '&:hover': {
                            borderColor: 'rgba(255, 255, 255, 0.5)',
                            bgcolor: 'rgba(255, 255, 255, 0.05)'
                          }
                        }}
                      >
                        {showAllReviews 
                          ? t('spaceSearch.showLess', 'Show less')
                          : t('spaceSearch.seeAllReviews', 'See all reviews')
                        }
                      </Button>
                    </Box>
                  )}
                </Box>
          </Box>
        )}
          </Grid>
        </Grid>

      </Box>
      {/* Booking dialog mounted at page root */}
      {selectedRoom && (
        <RoomBookingDialog
          open={bookingOpen}
          onClose={() => {
            setBookingOpen(false);
            setInitialStepKey(null);
            // Remove /book from URL when dialog is closed
            if (location.pathname.endsWith('/book') && roomName) {
              navigate(`/user/search/${city}/${spaceName}/${roomName}`, { replace: true });
            }
          }}
          room={selectedRoom}
          spaceName={space?.name}
          initialDateTime={initialDateTime}
          initialDuration={initialDuration}
          initialStepKey={initialStepKey}
        />
      )}
      
      {/* Amenity/Gear Details Popover */}
      <Popover
        open={Boolean(popoverAnchor)}
        anchorEl={popoverAnchor}
        onClose={handlePopoverClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        slotProps={{
          paper: {
            elevation: 3,
            sx: {
              borderRadius: 2,
              minWidth: 240,
              maxWidth: 320,
              mt: 1,
            }
          }
        }}
      >
        {popoverContent && (
          <Stack spacing={1.5} sx={{ p: 2 }}>
            {/* Header with icon and label */}
            <Stack direction="row" spacing={1} alignItems="center">
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'center',
                color: 'primary.main',
                '& .MuiSvgIcon-root': {
                  fontSize: '1.25rem'
                }
              }}>
                {popoverContent.icon}
              </Box>
              <Typography variant="subtitle2" sx={{ 
                fontSize: '0.875rem', 
                fontWeight: 600,
                color: 'text.primary',
                flex: 1
              }}>
                {popoverContent.label}
              </Typography>
            </Stack>

            {/* Details */}
            {popoverContent.details && (
              <>
                <Divider />
                <Typography variant="body2" sx={{ 
                  color: 'text.primary',
                  fontSize: '0.8rem',
                  lineHeight: 1.5
                }}>
                  {popoverContent.details}
                </Typography>
              </>
            )}

            {/* Price */}
            {popoverContent.price !== undefined && popoverContent.price > 0 && (
              <>
                <Divider />
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" sx={{ 
                    color: 'text.secondary',
                    fontSize: '0.65rem',
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    fontWeight: 600
                  }}>
                    {t('common.price', 'Price')}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    color: 'primary.main',
                    fontSize: '0.875rem',
                    fontWeight: 700
                  }}>
                    {new Intl.NumberFormat(undefined, {
                      style: 'currency',
                      currency: popoverContent.currency || 'USD',
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 2,
                    }).format(popoverContent.price)}/h
                  </Typography>
                </Stack>
              </>
            )}
          </Stack>
        )}
      </Popover>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbarOpen}
        message={snackbarMessage}
        severity="success"
        onClose={() => setSnackbarOpen(false)}
      />

      <PassPurchaseConfirmDialog
        open={passDialogOpen}
        offer={selectedPassOffer}
        onClose={() => setPassDialogOpen(false)}
        supportedPaymentMethods={resolvePassCheckoutPaymentMethodsForRooms(space?.rooms)}
        regulation={selectedPassOffer?.regulation ?? space?.regulation}
        termsName={space?.name}
        targetNames={
          selectedPassOffer
            ? resolvePassOfferTargetNames(selectedPassOffer)
            : undefined
        }
      />
    </>
  );
}
